// One canvas re-encode for images the browser shrinks before they leave it: an upload's downscale
// (media-upload.ts) and a staged photo's list thumbnail offline (BUG-2081).

/**
 * Re-encode an image as JPEG so its longest edge is at most `maxEdge`.
 *
 * @param input.image - the image bytes.
 * @param input.maxEdge - the longest edge allowed, in pixels.
 * @param input.quality - the JPEG quality, 0 to 1.
 * @returns the re-encoded JPEG, or null when the image already fits.
 * @throws When the browser can't decode the image, has no 2D canvas, or can't encode the result.
 */
export async function resizeImage(input: { image: Blob; maxEdge: number; quality: number }): Promise<Blob | null> {
  const bitmap = await createImageBitmap(input.image);
  try {
    const { width, height } = bitmap;
    const longestEdge = Math.max(width, height);
    if (longestEdge <= input.maxEdge) return null;
    const scale = input.maxEdge / longestEdge;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('resizeImage: no 2D canvas');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', input.quality));
    if (!blob) throw new Error('resizeImage: the canvas encoded nothing');
    return blob;
  } finally {
    bitmap.close();
  }
}
